<script setup lang="ts">
/**
 * Section FAQ canonique du site, charte « apple » de /dashboard (Karim
 * 2026-10-18 : « ça doit être comme dans /dashboard », sur toutes les pages) :
 * titre qui reste collé à gauche pendant qu'on lit, questions à droite
 * (FaqList : filets fins, rond « + » qui pivote), première question ouverte.
 * Sous 900 px, le titre passe au-dessus.
 *
 * Titre par défaut : « Questions fréquentes » (textes communs de l'accueil).
 * Slot par défaut : sous la liste (lien « Toutes les questions »…).
 * Le JSON-LD FAQPage reste posé par la page, avec le même tableau `items`.
 */
import { TITRE_SECTION_APPLE } from "~/utils/titresApple";

withDefaults(
  defineProps<{
    items: { question: string; answer: string }[];
    /** Titre propre à la page (défaut : « Questions fréquentes »). */
    titre?: string;
    /** Phrase sous le titre (facultative). */
    texte?: string;
    /** Ancre (barre des sections d'une page). */
    ancre?: string;
    /** Fond de la section : blanc (défaut) ou gris #f5f5f7 (comme /dashboard). */
    fond?: "blanc" | "gris";
  }>(),
  { titre: undefined, texte: undefined, ancre: undefined, fond: "blanc" },
);
</script>

<template>
  <section
    :id="ancre"
    class="scroll-mt-24 px-4 py-[72px] sm:px-8 lg:py-[100px]"
    :class="fond === 'gris' ? 'bg-[#f5f5f7]' : 'bg-white'"
  >
    <div class="faq-grille mx-auto max-w-[1120px]">
      <div class="faq-entete pa-reveal">
        <h2 class="text-[32px] text-[#1d1d1f] sm:text-[40px]" :style="TITRE_SECTION_APPLE">
          {{ titre ?? `${$t("accueil.faq.titre")} ${$t("accueil.faq.titreAccent")}` }}
        </h2>
        <p v-if="texte" class="mt-4 text-[17px] font-medium leading-[1.5] text-[#6e6e73]">{{ texte }}</p>
      </div>
      <div class="pa-reveal" style="--d: 100ms">
        <FaqList :items="items" premiere-ouverte />
        <div v-if="$slots.default" class="mt-8">
          <slot />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.faq-grille {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 72px;
  align-items: start;
}
/* Le titre reste à l'écran pendant qu'on parcourt les questions. */
.faq-entete {
  position: sticky;
  top: 112px;
}
@media (max-width: 900px) {
  .faq-grille {
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;
  }
  .faq-entete {
    position: static;
  }
}
</style>
